import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { Select, Spinner } from '@seismic/mantle';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { LoggingContext } from '../../context';
import { IRecordingState, Recorder } from '../../models';
import { IRecordingStateReducerAction } from '../../hooks';
import styles from './Setup.scss';
import SetupError from './SetupError';

interface SetupProp {
  state: IRecordingState;
  dispatch: React.Dispatch<IRecordingStateReducerAction>;
}

export default function Setup(props: SetupProp): ReactElement {
  const { t } = useTranslation();
  const { state } = props;
  const recorder = state.recorder;
  const logger = useContext(LoggingContext);
  const recorderElement = useRef<HTMLVideoElement>(null);
  const [permissionError, setPermissionError] = useState<boolean>(false);
  const [loadingDevices, setLoadingDevices] = useState<boolean>(true);
  const [isLoadingCamera, setIsLoadingCamera] = useState<boolean>(true);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<MediaDeviceInfo>();
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<MediaDeviceInfo>();
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);

  /**
   * Retrives the user's available audio and video devices
   */
  useEffect(() => {
    const getDevicesBy = (devices: MediaDeviceInfo[], kind: string): MediaDeviceInfo[] => {
      const filtered = devices.filter((d) => d.kind === kind);
      return uniqBy(filtered, 'groupId');
    };

    async function getMediaDevices() {
      setLoadingDevices(false);

      let allDevices = await navigator.mediaDevices.enumerateDevices();
      let audioDevices = getDevicesBy(allDevices, 'audioinput');
      let videoDevices = getDevicesBy(allDevices, 'videoinput');

      if (audioDevices.length <= 0 || videoDevices.length <= 0) {
        setPermissionError(true);
        setLoadingDevices(false);
        return;
      }

      const hasPermissions = audioDevices[0].label !== '' && videoDevices[0].label !== '';
      if (!hasPermissions) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          allDevices = await navigator.mediaDevices.enumerateDevices();
          audioDevices = getDevicesBy(allDevices, 'audioinput');
          videoDevices = getDevicesBy(allDevices, 'videoinput');

          setAudioDevices(audioDevices);
          setVideoDevices(videoDevices);
          stream.getTracks().forEach((t) => t.stop());
        } catch {
          setPermissionError(true);
        }
      } else {
        setAudioDevices(audioDevices);
        setVideoDevices(videoDevices);
      }

      setLoadingDevices(false);
    }

    getMediaDevices();
  }, []);

  /**
   * Defaults the currently selected audio and video device (if applicable)
   */
  useEffect(() => {
    const selectedAudioDevice = audioDevices.find((a) => a.deviceId === recorder.audioDeviceId);
    const selectedVideoDevice = videoDevices.find((a) => a.deviceId === recorder.videoDeviceId);
    var audioDevice = selectedAudioDevice ?? audioDevices[0];
    var videoDevice = selectedVideoDevice ?? videoDevices[0];
    setSelectedAudioDevice(audioDevice);
    setSelectedVideoDevice(videoDevice);
  }, [audioDevices, videoDevices, recorder]);

  /**
   * Starts the audio and video streams, and shows them to the user
   */
  useEffect(() => {
    async function startAudioVideoStream(recorderElement, recorder: Recorder) {
      try {
        await recorder.initializeRecorder();
        recorderElement.current.srcObject = null;
        recorderElement.current.srcObject = recorder.stream;
      } catch (err) {
        logger.logError('Error fetching media stream {Error}', [err]);
        setPermissionError(true);
      }
    }

    if (
      selectedAudioDevice &&
      selectedVideoDevice &&
      !loadingDevices &&
      recorderElement &&
      recorderElement.current &&
      recorder
    ) {
      recorder.audioDeviceId = selectedAudioDevice.deviceId;
      recorder.videoDeviceId = selectedVideoDevice.deviceId;
      startAudioVideoStream(recorderElement, recorder);
    }
  }, [selectedAudioDevice, selectedVideoDevice, loadingDevices, recorderElement, recorder, logger]);

  const isSelectDisabled = permissionError || loadingDevices || isLoadingCamera;
  const onVideoChange = (option: any) => setSelectedVideoDevice(JSON.parse(option.value));
  const onAudioChange = (option: any) => setSelectedAudioDevice(JSON.parse(option.value));
  const onLoadedMetadata = () => setIsLoadingCamera(false);

  return (
    <div className={styles.setupContainer} data-testid="setupContainer">
      <div className={styles.selectInputDevices}>
        <div className={styles.general}>{t('Setup.General', { defaultValue: 'General' })}</div>
        <span id="selectCamera" className={styles.selectCameraLabel}>
          {t('Setup.Camera', { defaultValue: 'Camera' })}
        </span>
        {/* TODO if options was typed correctly, value doesn't need to be stringified either */}
        <Select
          aria-labelledby="selectCamera"
          className={styles.selectCamera}
          data-testid="selectCamera"
          disabled={isSelectDisabled}
          options={videoDevices.map((v) => {
            return { label: v.label, value: JSON.stringify(v) };
          })}
          defaultOption={selectedVideoDevice}
          value={JSON.stringify(selectedVideoDevice)}
          onChange={onVideoChange}
        />
        <span id="selectMicrophone" className={styles.selectMicrophoneLabel}>
          {t('Setup.Microphone', { defaultValue: 'Microphone' })}
        </span>
        <Select
          aria-labelledby="selectMicrophone"
          className={styles.selectMicrophone}
          data-testid="selectMicrophone"
          disabled={isSelectDisabled}
          options={audioDevices.map((a) => {
            return { label: a.label, value: JSON.stringify(a) };
          })}
          defaultOption={selectedAudioDevice}
          value={JSON.stringify(selectedAudioDevice)}
          onChange={onAudioChange}
        />
      </div>
      <div className={styles.videoRecorderBackground}>
        {permissionError && <SetupError />}
        {!permissionError && (loadingDevices || isLoadingCamera) && (
          <div className={styles.checkPermissions} data-testid="loading-spinner">
            <Spinner size={32} />
          </div>
        )}
        {!permissionError && (!loadingDevices || !isLoadingCamera) && (
          <div className={styles.videoRecorderContainer} data-testid="video-recorder-container">
            <video
              id="recorder"
              onLoadedMetadata={onLoadedMetadata}
              className={styles.videoRecorder}
              data-testid="video-recorder"
              autoPlay
              muted
              ref={recorderElement}
            />
          </div>
        )}
      </div>
    </div>
  );
}
